import { useCallback, useEffect, useRef } from "react";
import { SMALL_VIEWPORT_MAX_WIDTH } from "config/constants";
import { useMediaQuery } from "hooks/useMediaQuery";
import { useUserWaypoints } from "hooks/useUserWaypoints";
import L from "leaflet";
import { useMap, useMapEvents } from "react-leaflet";
import { setHoveredTrackpoint } from "stores/genericStore/actions";
import {
  useGenericDispatch,
  useHoveredTrackpoint,
} from "stores/genericStore/GenericContext";
import { useRouting } from "stores/routingStore/RoutingContext";
import { Coords } from "types/app";
import { findHoveredTrackpoint } from "utils/map/findHoveredTrackpoint";
import { TrackPointMarker } from "../TrackPointMarker";

/** How close to the track line (in px) the cursor has to be, a bit more than the line's outline */
export const TRACK_HOVER_TOLERANCE = 10;

/** Only with a mouse: touch screens have no hover, and a tap on the line adds a point */
const HOVER_MEDIA_QUERY = `(hover: hover) and (pointer: fine) and (min-width: ${
  SMALL_VIEWPORT_MAX_WIDTH + 1
}px)`;

/**
 * The track's points are projected to pixels at zoom 0 once per track, the cursor and the tolerance are scaled
 * to it instead, so zooming doesn't project the whole track again (tens of ms for a long recording)
 */
const PROJECTION_ZOOM = 0;

type ProjectedTrack = {
  trackpoints: Coords[];
  points: L.Point[];
};

/**
 * Hovering the edited track's line with the mouse shows a circle on the line and the same point in the chart
 * (the reverse of hovering the chart). The hovered point is found from the map's mouse moves, so it works for
 * every way the line is drawn (routed, without routing, with a hotline).
 */
export const TrackHover = () => {
  const map = useMap();
  const {
    state: { computedTrackpoints },
  } = useRouting();
  const hoveredTrackpoint = useHoveredTrackpoint();
  const dispatch = useGenericDispatch();
  const { isAddingWaypoint } = useUserWaypoints();
  const canHover = useMediaQuery(HOVER_MEDIA_QUERY);

  const trackpointsRef = useRef(computedTrackpoints);
  const hoveredIndexRef = useRef<number | null>(null);
  const projectedRef = useRef<ProjectedTrack | null>(null);
  const frameRef = useRef<number | null>(null);
  const isEnabled = canHover && !isAddingWaypoint;
  const isEnabledRef = useRef(isEnabled);

  const setHovered = useCallback(
    (index: number | null) => {
      if (index === hoveredIndexRef.current) return;
      hoveredIndexRef.current = index;
      const position =
        index === null ? undefined : trackpointsRef.current[index];
      dispatch(
        setHoveredTrackpoint(
          index !== null && position ? { index, position } : null
        )
      );
    },
    [dispatch]
  );

  const clearHovered = useCallback(() => {
    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }
    // Dispatches only when a point was hovered, so mouse moves while dragging don't rerender anything
    setHovered(null);
  }, [setHovered]);

  // A changed track has its points elsewhere, the hovered point is found again on the next move
  useEffect(() => {
    trackpointsRef.current = computedTrackpoints;
    clearHovered();
  }, [clearHovered, computedTrackpoints]);

  useEffect(() => {
    isEnabledRef.current = isEnabled;
    if (!isEnabled) clearHovered();
  }, [clearHovered, isEnabled]);

  useEffect(() => clearHovered, [clearHovered]);

  /** The track's points in pixels at PROJECTION_ZOOM, projected once per track */
  const getProjectedPoints = () => {
    const trackpoints = trackpointsRef.current;
    const cached = projectedRef.current;
    if (cached?.trackpoints === trackpoints) return cached.points;
    const points = trackpoints.map((coords) =>
      map.project(coords, PROJECTION_ZOOM)
    );
    projectedRef.current = { trackpoints, points };
    return points;
  };

  useMapEvents({
    mousemove: (e) => {
      // A pressed button means a drag: of the map, a waypoint or the line (which inserts a waypoint)
      if (
        !isEnabledRef.current ||
        trackpointsRef.current.length < 2 ||
        e.originalEvent.buttons !== 0
      ) {
        clearHovered();
        return;
      }
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
      const { latlng } = e;
      // At most once per frame, the mouse moves more often than the screen is drawn
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = null;
        // The tolerance is in screen pixels, converted to pixels at PROJECTION_ZOOM
        const scale = map.getZoomScale(map.getZoom(), PROJECTION_ZOOM);
        setHovered(
          findHoveredTrackpoint(
            getProjectedPoints(),
            map.project(latlng, PROJECTION_ZOOM),
            TRACK_HOVER_TOLERANCE / scale
          )
        );
      });
    },
    mouseout: clearHovered,
    mousedown: clearHovered,
    dragstart: clearHovered,
    zoomstart: clearHovered,
  });

  return hoveredTrackpoint ? (
    <TrackPointMarker position={hoveredTrackpoint.position} />
  ) : null;
};
